import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import { useToast } from '@/components/ui/toastContext'
import { ACADEMIC_PHASES, type PhaseOrganization, type Program, type ProgramPhase } from '@/features/admin/api/programs.api'
import {
  useCreatePhase,
  useDeletePhase,
  useLmsLivePhasesQuery,
  usePhaseOrganizationsQuery,
  useProgramsQuery,
  useRefreshLmsLivePhases,
  useSyncPhaseOrganizations,
} from '@/features/admin/hooks/usePrograms'
import { getErrorMessage } from '@/utils/errors'

const phaseSchema = z.object({
  academic_phase: z.string().min(1, 'Phase is required'),
  organization_id: z.coerce.number().int().positive('Organisation is required'),
})
type PhaseFormValues = z.infer<typeof phaseSchema>

function ManageOrgsModal({
  program,
  phase,
  onClose,
}: {
  program: Program
  phase: ProgramPhase
  onClose: () => void
}) {
  const { data: orgs, isPending } = usePhaseOrganizationsQuery(program.id, phase.academic_phase)
  const sync = useSyncPhaseOrganizations(program.id, phase.academic_phase)
  const toast = useToast()
  const [selected, setSelected] = useState<Set<number> | null>(null)

  const effectiveSelected: Set<number> =
    selected ?? new Set((orgs ?? []).filter((o) => o.mapped).map((o) => o.id))

  const toggle = (org: PhaseOrganization) => {
    const next = new Set(effectiveSelected)
    if (next.has(org.id)) next.delete(org.id)
    else next.add(org.id)
    setSelected(next)
  }

  const save = () => {
    sync.mutate([...effectiveSelected], {
      onSuccess: () => {
        toast.success('Mapping saved.')
        onClose()
      },
      onError: (err) => toast.error(getErrorMessage(err)),
    })
  }

  return (
    <Modal
      show
      onClose={onClose}
      title={`${program.name} · ${phase.academic_phase}`}
      footer={
        <div className="d-flex gap-2 justify-content-end w-100">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={save} loading={sync.isPending}>Save Mapping</Button>
        </div>
      }
    >
      <p className="fs-13 text-muted mb-3">
        Select which organisations can post jobs and view candidates for this phase.
      </p>
      {isPending ? (
        <div className="text-center py-3">
          <span className="spinner-border spinner-border-sm" />
        </div>
      ) : (
        <>
          <div className="d-flex gap-3 mb-3">
            <button
              type="button"
              className="btn btn-link btn-sm p-0 fs-13"
              onClick={() => setSelected(new Set((orgs ?? []).map((o) => o.id)))}
            >
              Select all
            </button>
            <button
              type="button"
              className="btn btn-link btn-sm p-0 fs-13"
              onClick={() => setSelected(new Set())}
            >
              Clear all
            </button>
            <span className="ms-auto fs-12 text-muted">
              {effectiveSelected.size} of {(orgs ?? []).length} selected
            </span>
          </div>
          <div style={{ maxHeight: 360, overflowY: 'auto' }}>
            {(orgs ?? []).map((org) => (
              <div key={org.id} className="form-check mb-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id={`org-${org.id}`}
                  checked={effectiveSelected.has(org.id)}
                  onChange={() => toggle(org)}
                />
                <label className="form-check-label fs-13" htmlFor={`org-${org.id}`}>
                  {org.name}
                  {org.status !== 'approved' && (
                    <span className="badge bg-warning-transparent ms-2 fs-11">{org.status}</span>
                  )}
                </label>
              </div>
            ))}
          </div>
        </>
      )}
    </Modal>
  )
}

function LmsPhasesRow({ program }: { program: Program }) {
  const { data, isPending } = useLmsLivePhasesQuery(program.id)
  const refresh = useRefreshLmsLivePhases(program.id)

  return (
    <tr>
      <td colSpan={4} className="ps-4 py-2" style={{ background: 'rgba(var(--bs-primary-rgb), 0.04)' }}>
        <div className="d-flex align-items-center gap-2 flex-wrap fs-12">
          <span className="fw-semibold text-muted">
            <i className="ti ti-cloud-download me-1" />
            Phases on LMS:
          </span>
          {isPending || refresh.isPending ? (
            <span className="spinner-border spinner-border-sm text-muted" role="status" aria-label="Loading phases" />
          ) : data?.error ? (
            <span className="text-danger">
              <i className="ti ti-alert-triangle me-1" />
              {data.error} Showing the standard phase list instead.
            </span>
          ) : (data?.phases ?? []).length === 0 ? (
            <span className="text-muted">The LMS reported no phases.</span>
          ) : (
            (data?.phases ?? []).map((p) => (
              <span
                key={p.academic_phase}
                className={`badge fs-11 ${p.is_active ? 'bg-success-transparent text-success' : 'bg-light text-muted border'}`}
                title={p.phase_id ? `Phase ID: ${p.phase_id}` : undefined}
              >
                {p.academic_phase}
                {p.is_active && ' · active'}
              </span>
            ))
          )}
          <button
            type="button"
            className="btn btn-link btn-sm p-0 fs-12 ms-1"
            onClick={() => refresh.mutate()}
            disabled={refresh.isPending}
          >
            <i className="ti ti-refresh me-1" />
            Refresh
          </button>
        </div>
      </td>
    </tr>
  )
}

function AddPhaseRow({
  program,
  onDone,
}: {
  program: Program
  onDone: () => void
}) {
  const { data: live } = useLmsLivePhasesQuery(program.id)
  const phaseOptions =
    live && !live.error && live.phases.length > 0
      ? live.phases.map((p) => p.academic_phase)
      : [...ACADEMIC_PHASES]
  const createPhase = useCreatePhase(program.id)
  const toast = useToast()

  const { register, handleSubmit, formState: { errors } } = useForm<
    z.input<typeof phaseSchema>,
    unknown,
    PhaseFormValues
  >({
    resolver: zodResolver(phaseSchema),
    defaultValues: { academic_phase: '', organization_id: 0 },
  })

  const onSubmit = handleSubmit((values) => {
    createPhase.mutate(
      { academic_phase: values.academic_phase, organization_id: values.organization_id },
      {
        onSuccess: () => {
          onDone()
          toast.success('Phase mapping added.')
        },
        onError: (err) => toast.error(getErrorMessage(err)),
      },
    )
  })

  return (
    <tr>
      <td colSpan={4} className="py-2 px-3" style={{ background: 'var(--bs-light)' }}>
        <form onSubmit={(e) => void onSubmit(e)} noValidate>
          <div className="d-flex gap-2 align-items-start flex-wrap">
            <div style={{ minWidth: 160 }}>
              <select className={`form-select form-select-sm${errors.academic_phase ? ' is-invalid' : ''}`} {...register('academic_phase')}>
                <option value="">— Select phase —</option>
                {phaseOptions.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
              {errors.academic_phase && (
                <div className="invalid-feedback">{errors.academic_phase.message}</div>
              )}
            </div>
            <div style={{ minWidth: 220 }}>
              <input
                type="number"
                className={`form-control form-control-sm${errors.organization_id ? ' is-invalid' : ''}`}
                placeholder="Organisation ID"
                {...register('organization_id')}
              />
              {errors.organization_id && (
                <div className="invalid-feedback">{errors.organization_id.message}</div>
              )}
            </div>
            <div className="d-flex gap-2 mt-1">
              <Button type="submit" loading={createPhase.isPending}>Add Mapping</Button>
              <Button variant="secondary" onClick={onDone}>Cancel</Button>
            </div>
          </div>
          <p className="fs-12 text-muted mt-1 mb-0">
            Tip: to map multiple orgs to a phase at once, add one first, then use <strong>Manage Orgs</strong>.
          </p>
        </form>
      </td>
    </tr>
  )
}

function ProgramRow({ program }: { program: Program }) {
  const [expanded, setExpanded] = useState(false)
  const [showAddPhase, setShowAddPhase] = useState(false)
  const [managingPhase, setManagingPhase] = useState<ProgramPhase | null>(null)
  const deletePhase = useDeletePhase(program.id)
  const toast = useToast()

  const onDeletePhase = (phase: ProgramPhase) => {
    if (!confirm(`Delete all org mappings for phase "${phase.academic_phase}" in ${program.name}?\nThis cannot be undone.`)) return
    deletePhase.mutate(phase.academic_phase, {
      onSuccess: () => toast.success('Phase and all its mappings deleted.'),
      onError: (err) => toast.error(getErrorMessage(err)),
    })
  }

  return (
    <>
      <tr style={{ height: 56 }}>
        <td>
          <button
            type="button"
            className="btn btn-link p-0 text-start fw-semibold fs-14 text-decoration-none"
            onClick={() => setExpanded((v) => !v)}
          >
            <i className={`ti ti-chevron-${expanded ? 'down' : 'right'} me-2 fs-14`} />
            {program.name}
          </button>
        </td>
        <td>
          <code className="fs-12">{program.code}</code>
        </td>
        <td>
          <span className="badge bg-primary-transparent fs-12">
            {program.phases.length} {program.phases.length === 1 ? 'phase' : 'phases'}
          </span>
        </td>
        <td className="text-end">
          <Button
            variant="secondary"
            onClick={() => { setExpanded(true); setShowAddPhase(true) }}
          >
            <i className="ti ti-plus me-1" />
            Add Phase
          </Button>
        </td>
      </tr>

      {expanded && <LmsPhasesRow program={program} />}

      {expanded && program.phases.map((phase) => (
        <tr key={phase.academic_phase} style={{ height: 52, background: 'rgba(var(--bs-primary-rgb), 0.02)' }}>
          <td className="ps-4">
            <span className="fw-medium fs-13">
              <i className="ti ti-calendar-stats me-2 text-muted" />
              {phase.academic_phase}
            </span>
          </td>
          <td />
          <td>
            <span className="badge bg-success-transparent fs-12">
              <i className="ti ti-building me-1" />
              {phase.organization_count} org{phase.organization_count !== 1 ? 's' : ''}
            </span>
            {phase.active_count < phase.organization_count && (
              <span className="badge bg-warning-transparent fs-12 ms-1">
                {phase.organization_count - phase.active_count} inactive
              </span>
            )}
          </td>
          <td className="text-end">
            <div className="d-flex gap-2 justify-content-end">
              <Button
                variant="secondary"
                onClick={() => setManagingPhase(phase)}
              >
                <i className="ti ti-users me-1" />
                Manage Orgs
              </Button>
              <Button
                variant="danger"
                onClick={() => onDeletePhase(phase)}
                loading={deletePhase.isPending}
              >
                <i className="ti ti-trash" />
              </Button>
            </div>
          </td>
        </tr>
      ))}

      {expanded && program.phases.length === 0 && !showAddPhase && (
        <tr>
          <td colSpan={4} className="ps-4 fs-13 text-muted py-2">
            No phases yet. Click <strong>Add Phase</strong> to create one.
          </td>
        </tr>
      )}

      {expanded && showAddPhase && (
        <AddPhaseRow program={program} onDone={() => setShowAddPhase(false)} />
      )}

      {managingPhase && (
        <ManageOrgsModal
          program={program}
          phase={managingPhase}
          onClose={() => setManagingPhase(null)}
        />
      )}
    </>
  )
}

export function ProgramsPage() {
  const { data: programs, isPending } = useProgramsQuery()

  return (
    <>
      <div className="my-4 d-flex align-items-center justify-content-between flex-wrap gap-2 page-header-breadcrumb">
        <div>
          <p className="fw-semibold fs-20 mb-1">Programs</p>
          <p className="text-muted fs-13 mb-0">
            Map recruiter organisations to LMS program phases. Only approved organisations with an
            active mapping can post jobs and receive applications for a given phase.
          </p>
        </div>
      </div>

      <div className="card custom-card">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead className="sticky-top bg-white">
              <tr>
                <th className="fs-13">Program (LMS Source)</th>
                <th className="fs-13">Code</th>
                <th className="fs-13">Phases</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {isPending && (
                <tr>
                  <td colSpan={4} className="text-center py-4">
                    <span className="spinner-border spinner-border-sm" role="status" />
                  </td>
                </tr>
              )}
              {!isPending && (programs ?? []).length === 0 && (
                <tr>
                  <td colSpan={4} className="text-muted text-center fs-13 py-4">
                    No active LMS sources found. Add one in{' '}
                    <a href="/admin/lms-sources">LMS Integrations</a> first.
                  </td>
                </tr>
              )}
              {(programs ?? []).map((program) => (
                <ProgramRow key={program.id} program={program} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  )
}
